import { useTranslation } from "react-i18next";
import '../styles/buttons.css';

interface Tab {
    id: string;
    titleKey: string;
}

const upcomingTab: Tab = { id: 'upcoming', titleKey: 'upcoming' };
const liveTab: Tab = { id: 'live', titleKey: 'live' };
const resultsTab: Tab = { id: 'results', titleKey: 'results' };

interface MatchTabsProps {
    activeTab: string;
    onTabChange: (tabId: string) => void;
    /** Maps being played right now. Pass it to get a Live tab between Kommende and Resultater;
     *  it shows while anything is live (and while it is the open tab). */
    liveCount?: number;
}

const MatchTabs: React.FC<MatchTabsProps> = ({ activeTab, onTabChange, liveCount }) => {
    const { t } = useTranslation('pages');
    const showLive = liveCount !== undefined && (liveCount > 0 || activeTab === liveTab.id);
    const tabs = showLive ? [upcomingTab, liveTab, resultsTab] : [upcomingTab, resultsTab];

    return (
        <div className="match-tabs-container">
            <ul className="nav d-flex justify-content-center">
                {tabs.map((tab) => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`btn nav-link ${activeTab === tab.id ? 'active' : ''} m-1`}
                            onClick={() => {
                                onTabChange(tab.id);
                            }}
                            style={{
                                background: activeTab === tab.id
                                    ? 'linear-gradient(135deg, #ff6600, #ff9933)'
                                    : 'rgba(255, 255, 255, 0.05)',
                                color: activeTab === tab.id
                                    ? '#ffffff'
                                    : 'rgba(255, 255, 255, 0.8)',
                                border: activeTab === tab.id
                                    ? '1px solid transparent'
                                    : '1px solid rgba(255, 102, 0, 0.2)',
                                borderRadius: '8px',
                                padding: '10px 24px',
                                fontWeight: '600',
                                transition: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                cursor: 'pointer',
                                position: 'relative' as const,
                                overflow: 'hidden',
                                boxShadow: activeTab === tab.id
                                    ? '0 1px 4px rgba(255, 102, 0, 0.1)'
                                    : 'none',
                                margin: '0 5px'
                            }}
                            onMouseEnter={(e) => {
                                if (activeTab !== tab.id) {
                                    e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                                    e.currentTarget.style.borderColor = 'rgba(255, 102, 0, 0.4)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(255, 102, 0, 0.1)';
                                    e.currentTarget.style.color = '#ff9933';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (activeTab !== tab.id) {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                    e.currentTarget.style.borderColor = 'rgba(255, 102, 0, 0.2)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                                }
                            }}
                        >
                            {tab.id === liveTab.id && (
                                <span
                                    aria-hidden
                                    style={{
                                        display: 'inline-block',
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        // White on the active orange tab, where red would vanish.
                                        background: activeTab === tab.id ? '#ffffff' : '#ef4444',
                                        marginRight: 8,
                                        verticalAlign: 'middle',
                                    }}
                                />
                            )}
                            {t(`matches.${tab.titleKey}`)}
                            {tab.id === liveTab.id && !!liveCount && (
                                <span style={{ marginLeft: 8, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>{liveCount}</span>
                            )}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default MatchTabs;
